import { onCleanup } from "solid-js";

/**
 * Motion budget M5: 20 ms past the 200 ms of the shell, so the scroll measures
 * the final height. Same delay on every device and with reduced motion: a
 * heading opens no keyboard, so the desktop guard of `autofocusSoft` is not
 * needed here.
 */
export const REVEAL_AFTER_MS = 220;

type VerticalBox = { top: number; bottom: number };

/**
 * The gap kept above a revealed section, the `scroll-mt-6` of `SlotSection`
 * (24 px), measured from the bottom edge of a fixed header if there is one.
 */
const SCROLL_MARGIN = 24;

/**
 * The section is not entirely inside the visible part of the scroll
 * container: it starts above it or under a fixed header (`topInset`), or it
 * ends under its bottom edge minus a fixed bar (`bottomInset`).
 */
export function isOutOfView(
  section: VerticalBox,
  container: VerticalBox,
  bottomInset: number,
  topInset = 0,
): boolean {
  return (
    section.top < container.top + topInset ||
    section.bottom > container.bottom - bottomInset
  );
}

/**
 * The nearest ancestor that really scrolls (`DialogContent` in the modal, the
 * route's scroll area in the onboarding), `null` when it is the document. The
 * morph shell is `overflow: hidden` and never matches.
 */
function scrollContainerOf(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
  }
  return null;
}

function boxOf(container: HTMLElement | null): VerticalBox {
  if (!container) {
    return { top: 0, bottom: document.documentElement.clientHeight };
  }
  const rect = container.getBoundingClientRect();
  return { top: rect.top, bottom: rect.bottom };
}

export type RevealTarget = {
  /** The element that takes the focus: a section title or a tile. */
  focus: HTMLElement;
  /** What the scroll brings to the top of the visible area; default `focus`. */
  section?: HTMLElement;
  /** The whole section must be in view (the secondary, AC6); otherwise only `focus`. */
  whole?: boolean;
};

export type RevealOptions = {
  /** Pixels of the scroll container covered at the bottom by a fixed bar. */
  bottomInset?: () => number;
  /** Pixels of the scroll container covered at the top by a fixed header. */
  topInset?: () => number;
};

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * M5 as one helper. `reveal(resolve)`:
 * 1. moves the focus to the target at once, without scrolling, so the pressed
 *    control that leaves with the outgoing face never hands the focus to
 *    `body` (AC26);
 * 2. one `setTimeout` at 220 ms, after the morph: only when the target is out
 *    of the visible part of its scroll container (minus `topInset`, the fixed
 *    header, and `bottomInset`, the fixed bar of the onboarding), the
 *    container scrolls so the section starts 24 px under the header, `smooth`
 *    or `auto` with reduced motion; then `focus({ preventScroll: true })` in
 *    the same tick, so the focus never interrupts the smooth scroll (R4).
 *
 * The scroll is done by hand on the nearest scroll container, not with
 * `scrollIntoView`: a fixed header is not something `scroll-margin` knows
 * about, and `scrollIntoView` would also scroll the `overflow: hidden` morph
 * shell and every outer scroller.
 *
 * `resolve` is read at both moments: the refs exist only once the new face is
 * mounted. A new `reveal` replaces the pending one; the timer is cleared on
 * cleanup.
 */
export function useRevealSecondary(options: RevealOptions = {}) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => clearTimeout(timer);
  onCleanup(cancel);

  const reveal = (resolve: () => RevealTarget | null) => {
    cancel();
    queueMicrotask(() => resolve()?.focus.focus({ preventScroll: true }));
    timer = setTimeout(() => {
      const target = resolve();
      if (!target) return;
      const section = target.section ?? target.focus;
      const measured = target.whole ? section : target.focus;
      const container = scrollContainerOf(measured);
      const box = boxOf(container);
      const topInset = options.topInset?.() ?? 0;
      const bottomInset = options.bottomInset?.() ?? 0;
      if (
        isOutOfView(
          measured.getBoundingClientRect(),
          box,
          bottomInset,
          topInset,
        )
      ) {
        const scroll: ScrollToOptions = {
          top:
            section.getBoundingClientRect().top -
            (box.top + topInset + SCROLL_MARGIN),
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        };
        if (container) container.scrollBy(scroll);
        else window.scrollBy(scroll);
      }
      target.focus.focus({ preventScroll: true });
    }, REVEAL_AFTER_MS);
  };

  return { reveal, cancel };
}
